import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import './styles/Results.css';

const backendBaseUrl = process.env.REACT_APP_BACKEND_URL;
const backendURL = backendBaseUrl
  ? `${backendBaseUrl.replace(/\/$/, '')}/run`
  : null;
const FORM_COOKIE_KEY = 'dd214_form_data';
const RESULTS_COOKIE_KEY = 'dd214_last_results';

const getCookieValue = (name) => {
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];

  return cookieValue ? decodeURIComponent(cookieValue) : null;
};

const clearSavedFormCookie = () => {
  document.cookie = `${FORM_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

const clearResultsCookie = () => {
  document.cookie = `${RESULTS_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

const persistResultsToCookie = (data) => {
  try {
    const oneDayInSeconds = 60 * 60 * 24;
    document.cookie = `${RESULTS_COOKIE_KEY}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${oneDayInSeconds}; SameSite=Lax`;
  } catch (error) {
    console.error('Failed to persist results cookie:', error);
  }
};

const getSavedResultsFromCookie = () => {
  try {
    const cookieValue = getCookieValue(RESULTS_COOKIE_KEY);
    if (!cookieValue) return null;
    return JSON.parse(cookieValue);
  } catch (error) {
    console.error('Failed to parse results cookie:', error);
    return null;
  }
};

const normalizeBackendPayload = (payload) => {
  if (payload === null || payload === undefined) return '';

  if (typeof payload !== 'string') {
    return payload.html || payload;
  }

  const trimmed = payload.trim();
  if (!trimmed) return '';

  try {
    const maybeJson = JSON.parse(trimmed);
    return maybeJson.html || maybeJson;
  } catch {
    return trimmed;
  }
};

const extractStylesAndRenderableHtml = (html) => {
  if (typeof html !== 'string') {
    return {
      stylesReady: false,
      cssBlock: '',
      renderHtml: ''
    };
  }

  const styleStartIndex = html.search(/<style\b[^>]*>/i);
  if (styleStartIndex === -1) {
    return {
      stylesReady: false,
      cssBlock: '',
      renderHtml: ''
    };
  }

  const styleCloseIndex = html.indexOf('</style>', styleStartIndex);
  if (styleCloseIndex === -1) {
    return {
      stylesReady: false,
      cssBlock: '',
      renderHtml: ''
    };
  }

  const cssBlock = html.slice(styleStartIndex, styleCloseIndex + '</style>'.length);
  const renderHtml = `${html.slice(0, styleStartIndex)}${html.slice(styleCloseIndex + '</style>'.length)}`;

  return {
    stylesReady: true,
    cssBlock,
    renderHtml
  };
};

const countHtmlLines = (html) => {
  if (typeof html !== 'string' || !html) return 0;
  return html.split(/\r?\n/).length;
};

function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const handleReload = React.useCallback(() => {
    window.location.reload();
  }, []);
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState(false);
  const [showInputData, setShowInputData] = React.useState(false);
  const initialResults = location.state?.results ?? getSavedResultsFromCookie();
  const formData = location.state?.formData;
  const shouldStreamFromBackend = Boolean(location.state?.streamFromBackend && formData && !initialResults);
  const [results, setResults] = React.useState(initialResults);
  const [streamCssBlock, setStreamCssBlock] = React.useState('');
  const [streamRenderHtml, setStreamRenderHtml] = React.useState('');
  const [hasStreamStyles, setHasStreamStyles] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(shouldStreamFromBackend);
  const [streamError, setStreamError] = React.useState('');
  const lastRenderedLineCountRef = React.useRef(0);
  const [loadingDotCount, setLoadingDotCount] = React.useState(1);

  const extractPrintableHtml = React.useCallback(() => {
    if (typeof results === 'string' && results.trim()) {
      return results;
    }

    if (!results && (streamCssBlock || streamRenderHtml)) {
      return `${streamCssBlock}${streamRenderHtml}`;
    }

    if (results && typeof results !== 'string') {
      return `<pre>${JSON.stringify(results, null, 2)}</pre>`;
    }

    return '';
  }, [results, streamCssBlock, streamRenderHtml]);

  const handleSaveAsPdf = React.useCallback(async () => {
    const htmlPayload = extractPrintableHtml();
    if (!htmlPayload || !backendURL) return;

    try {
      setIsGeneratingPdf(true);

      const response = await fetch(`${backendBaseUrl.replace(/\/$/, '')}/OutputPDF`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          html: htmlPayload,
          fileName: 'dd214-analysis'
        })
      });

      if (!response.ok) {
        throw new Error(`PDF generation failed (${response.status})`);
      }

      const pdfBlob = await response.blob();
      const objectUrl = window.URL.createObjectURL(pdfBlob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = 'dd214-analysis.pdf';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Save as PDF failed:', error);
      setStreamError((prev) => prev || 'Unable to save PDF right now. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [extractPrintableHtml]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  React.useEffect(() => {
    if (!isStreaming) return;

    const intervalId = window.setInterval(() => {
      setLoadingDotCount((prev) => (prev % 3) + 1);
    }, 450);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isStreaming]);

  React.useEffect(() => {
    if (!shouldStreamFromBackend) return;

    let isMounted = true;

    const streamResults = async () => {
      setIsStreaming(true);
      setStreamError('');
      setStreamCssBlock('');
      setStreamRenderHtml('');
      setHasStreamStyles(false);
      lastRenderedLineCountRef.current = 0;

      try {
        if (!backendURL) {
          throw new Error('Missing REACT_APP_BACKEND_URL environment variable');
        }

        const response = await fetch(backendURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (!isMounted) return;
          setResults(normalizeBackendPayload(data));
          clearSavedFormCookie();
          return;
        }

        if (!response.body || typeof response.body.getReader !== 'function') {
          const fallbackText = await response.text();
          if (!isMounted) return;
          setResults(normalizeBackendPayload(fallbackText));
          clearSavedFormCookie();
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          accumulated += chunk;
          if (isMounted && chunk) {
            const extracted = extractStylesAndRenderableHtml(accumulated);
            if (extracted.stylesReady) {
              const nextLineCount = countHtmlLines(extracted.renderHtml);
              const shouldRenderNow =
                lastRenderedLineCountRef.current === 0 ||
                nextLineCount - lastRenderedLineCountRef.current >= 25;

              if (shouldRenderNow) {
                setHasStreamStyles(true);
                setStreamCssBlock(extracted.cssBlock);
                setStreamRenderHtml(extracted.renderHtml);
                lastRenderedLineCountRef.current = nextLineCount;
              }
            }
          }
        }

        accumulated += decoder.decode();

        if (!isMounted) return;

        const finalExtracted = extractStylesAndRenderableHtml(accumulated);
        if (finalExtracted.stylesReady) {
          setHasStreamStyles(true);
          setStreamCssBlock(finalExtracted.cssBlock);
          setStreamRenderHtml(finalExtracted.renderHtml);
          lastRenderedLineCountRef.current = countHtmlLines(finalExtracted.renderHtml);
        }

        const normalized = normalizeBackendPayload(accumulated);
        if (!normalized) {
          throw new Error('Empty streamed response from backend');
        }

        setResults(normalized);
        clearSavedFormCookie();
      } catch (error) {
        if (!isMounted) return;
        setStreamError(error?.message || 'Failed to stream analysis from backend');
      } finally {
        if (isMounted) {
          setIsStreaming(false);
        }
      }
    };

    streamResults();

    return () => {
      isMounted = false;
    };
  }, [formData, shouldStreamFromBackend]);

  React.useEffect(() => {
    if (!results || isStreaming) return;
    persistResultsToCookie(results);
  }, [results, isStreaming]);

  React.useEffect(() => {
    if (results || isStreaming || shouldStreamFromBackend || streamRenderHtml || hasStreamStyles) return;
    navigate('/questionnaire', { replace: true });
  }, [navigate, results, isStreaming, shouldStreamFromBackend, streamRenderHtml, hasStreamStyles]);

  if (!results && !isStreaming && !streamRenderHtml && !hasStreamStyles) {
    return (
      <div className="results-page">
        <div className="results-shell results-shell-narrow">
          <div className="results-panel results-empty">
            <div className="results-empty-icon">📋</div>
            <h1>No Results Available</h1>
            <p>
              {streamError || 'No data was provided. Please submit the questionnaire first.'}
            </p>
            {streamError && (
              <button
                onClick={handleReload}
                className="results-btn results-btn-dark"
              >
                Reload
              </button>
            )}
            <button
              onClick={() => {
                clearResultsCookie();
                navigate('/');
              }}
              className="results-btn results-btn-primary"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatFieldName = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'Not provided';
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const categorizeResults = (data) => {
    const categories = {
      military: [
        'currentRank', 'timeInService', 'activeDuty', 'branchOfService',
        'dutyLocation', 'contractEndDate', 'mos', 'qualifications',
        'evalTrend', 'hasDependents'
      ],
      civilian: [
        'bachelorDegrees', 'postgradDegrees', 'civilianCertifications',
        'nonMilitaryJobs', 'otherSkills'
      ],
      other: [
        'liquidSavings', 'yearsToRetirement', 'primaryMotivation',
        'pursueEducation', 'educationInCareerField', 'vaDisabilityRating',
        'dependentsWithDisabilities', 'qualifiesForGiBill', 'ownsHome',
        'recurringExpenses', 'militaryComfortLevel', 'willingnessToRelocate',
        'workStyle', 'workType', 'familyTimeImportance'
      ]
    };

    const categorized = {
      military: {},
      civilian: {},
      other: {},
      analysis: {}
    };

    Object.entries(data).forEach(([key, value]) => {
      if (categories.military.includes(key)) {
        categorized.military[key] = value;
      } else if (categories.civilian.includes(key)) {
        categorized.civilian[key] = value;
      } else if (categories.other.includes(key)) {
        categorized.other[key] = value;
      } else {
        categorized.analysis[key] = value;
      }
    });

    return categorized;
  };

  const categorizedData = formData ? categorizeResults(formData) : categorizeResults(results);

  const renderDataSection = (title, data) => {
    if (Object.keys(data).length === 0) return null;

    return (
      <details className="results-info-section" open>
        <summary className="results-section-title">{title}</summary>
        <div className="results-info-list">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="results-info-row">
              <div className="results-info-label">
                {formatFieldName(key)}
              </div>
              <div className="results-info-value">
                {formatValue(value)}
              </div>
            </div>
          ))}
        </div>
      </details>
    );
  };

  return (
    <div className="results-page">
      <div className="results-shell">
        <div className="results-panel results-header">
          <h1>
            Analysis Complete!
          </h1>
          <p>
            Here's your personalized military transition analysis
          </p>
          <div className="results-actions">
            <button
              onClick={handleSaveAsPdf}
              className="results-btn results-btn-dark"
              disabled={isGeneratingPdf || (!results && !streamRenderHtml)}
            >
              {isGeneratingPdf ? 'Preparing PDF...' : 'Save as PDF'}
            </button>
            <button
              onClick={() => setShowInputData((prev) => !prev)}
              className="results-btn results-btn-dark"
              disabled={!formData}
            >
              {showInputData ? 'Hide Input' : 'Show Input'}
            </button>
            <button
              onClick={() => {
                clearResultsCookie();
                navigate('/questionnaire', { replace: true, state: null });
              }}
              className="results-btn results-btn-dark"
            >
              Retake Questionnaire
            </button>
            <button
              onClick={() => {
                clearResultsCookie();
                navigate('/');
              }}
              className="results-btn results-btn-dark"
            >
              Back to Homepage
            </button>
          </div>
        </div>

        {results && (
          <div className="results-panel">
            <h3 className="results-analysis-title">
              Analysis & Recommendations
            </h3>
            <div className="results-analysis-body" id="ai-results-shell">
              {(() => {
                if (typeof results === 'string') {
                  const bodyMatch = results.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                  if (bodyMatch) {
                    return parse(bodyMatch[1]);
                  }
                  return parse(results);
                }
                return JSON.stringify(results);
              })()}
            </div>
          </div>
        )}

        {!results && isStreaming && (
          <div className="results-panel">
            <h3 className="results-analysis-title">Analysis In Progress</h3>
            <div className="results-analysis-body">
              <div className="results-stream-status">
                {hasStreamStyles
                  ? `Rendering analysis sections${'.'.repeat(loadingDotCount)}`
                  : `Loading, please wait${'.'.repeat(loadingDotCount)}`}
              </div>
              {hasStreamStyles ? (
                <div className="results-stream-render-shell">
                  {parse(`${streamCssBlock}${streamRenderHtml}`)}
                </div>
              ) : (
                <div className="results-stream-waiting">Preparing analysis layout...</div>
              )}
            </div>
          </div>
        )}

        {!results && !isStreaming && hasStreamStyles && (
          <div className="results-panel">
            <h3 className="results-analysis-title">Partial Analysis</h3>
            <div className="results-analysis-body">
              {streamError && (
                <>
                  <div className="results-stream-error">{streamError}</div>
                  <button
                    onClick={handleReload}
                    className="results-btn results-btn-dark"
                  >
                    Reload
                  </button>
                </>
              )}
              <div className="results-stream-render-shell">
                {parse(`${streamCssBlock}${streamRenderHtml}`)}
              </div>
            </div>
          </div>
        )}

        {formData && showInputData && (
          <div className="results-panel">
            <h2 className="results-user-info-title">
              Your Information
            </h2>

            <div className="results-user-info-sections">
              {renderDataSection('Military Background', categorizedData.military)}
              {renderDataSection('Civilian Experience', categorizedData.civilian)}
              {renderDataSection('Additional Information', categorizedData.other)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Results;
